import React, { useState } from "react";
import DayPicker from "react-day-picker";
import styles from './NewMobileAssets.module.css';
import logo from '../Header/JPG_as-logo-color-vertical Copy.jpg';
import Menu from '../Menu/Menu';
import '../Calendar/Calendar.scss';

const formatCurrency = (value) => `$${new Intl.NumberFormat().format(Math.round(Number(value) || 0))}`;

const getPayoffDate = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() + Number(months || 0));
    return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const getAgeAtDate = (dateOfBirth, date) => {
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) {
        return "";
    }
    let age = date.getFullYear() - birthDate.getFullYear();
    const beforeBirthday =
        date.getMonth() < birthDate.getMonth() ||
        (date.getMonth() === birthDate.getMonth() && date.getDate() < birthDate.getDate());
    if (beforeBirthday) {
        age -= 1;
    }
    return age;
};

const amortizeLoan = (debt, rate, monthlyPayment, extraPayment) => {
    let balance = Number(debt) || 0;
    let months = 1;
    while (balance > 0 && months < 360) {
        balance -= monthlyPayment + extraPayment;
        balance *= 1 + (Number(rate) || 0) / 1200;
        months += 1;
    }
    return months;
};

const getCalendarDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    const timezoneOffset = date.getTime() - utcDate.getTime();
    return new Date(date.getTime() - timezoneOffset);
};

const formatCalendarDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString("en-US", { timeZone: "UTC" });
};

export default function NewMobileMain({ data }) {
    const [extraPayment, setExtraPayment] = useState(500);
    const loanData = data.item;
    const aggregate = loanData.loan_aggregate;
    const user = data.userInfo[0];
    const payoffDate = getPayoffDate(aggregate.estimated_months_to_payoff);
    const payoffDateValue = new Date();
    payoffDateValue.setMonth(
        payoffDateValue.getMonth() + Number(aggregate.estimated_months_to_payoff || 0)
    );
    const payoffAge = getAgeAtDate(user.userDob, payoffDateValue);
    const providers = loanData.student_loan_info.reduce((summary, loan) => {
        const provider = summary[loan.servicer_name] || {
            name: loan.servicer_name,
            balance: 0,
            interest: 0,
            count: 0,
        };
        provider.balance += Number(loan.current_balance) || 0;
        provider.interest += Number(loan.interest_rate) || 0;
        provider.count += 1;
        summary[loan.servicer_name] = provider;
        return summary;
    }, {});
    const providerRows = Object.values(providers).map((provider) => ({
        ...provider,
        interest: provider.count ? provider.interest / provider.count : 0,
    }));
    const sortedCalendarLoans = [...loanData.student_loan_info].sort((first, second) =>
        first.servicer_name.toUpperCase().localeCompare(second.servicer_name.toUpperCase())
    );
    const calendarRows = [];
    sortedCalendarLoans.forEach((loan) => {
        const previousRow = calendarRows[calendarRows.length - 1];
        if (!previousRow || previousRow.servicerId !== loan.servicer_id) {
            calendarRows.push({
                providerId: calendarRows.length,
                servicerId: loan.servicer_id,
                name: loan.servicer_name,
                amount: Number(loan.est_monthly_payment) || 0,
                dueDate: loan.next_payment_date,
            });
        } else {
            previousRow.amount += Number(loan.est_monthly_payment) || 0;
        }
    });
    const calendarDates = {};
    const calendarDateStyles = {};
    const calendarColors = ["#288B6A", "#9B9B9B", "#232931"];
    calendarRows.forEach((row, index) => {
        const calendarDate = getCalendarDate(row.dueDate);
        if (calendarDate) {
            const colorIndex = index < 3 ? index : index % 3;
            calendarDates[row.providerId] = calendarDate;
            calendarDateStyles[row.providerId] = {
                color: colorIndex === 1 ? "black" : "white",
                backgroundColor: calendarColors[colorIndex],
                backgroundClip: "content-box",
            };
        }
    });
    const principalPaid = aggregate.total_principal_paid;
    const totalPrincipal = Number(aggregate.total_principal) || 0;
    const principalPaidRatio = totalPrincipal
        ? Math.min(1, Math.max(0, Number(principalPaid) / totalPrincipal))
        : 0;
    const principalRemainingRatio = 1 - principalPaidRatio;
    const principalRemaining = Math.max(0, totalPrincipal - Number(principalPaid || 0));
    const totalInterest = Number(aggregate.total_interest_paid || 0) +
        Number(aggregate.total_outstanding_interest || 0);
    const interestPaid = Number(aggregate.total_interest_paid || 0);
    const interestPaidRatio = totalInterest
        ? Math.min(1, Math.max(0, interestPaid / totalInterest))
        : 0;
    const interestRemainingRatio = 1 - interestPaidRatio;
    const monthlyPayment = Math.max(
        Number(aggregate.total_actual_monthly_payment) || 0,
        Number(aggregate.total_min_payment) || 0
    );
    const payoffMonths = Number(aggregate.estimated_months_to_payoff) || 0;
    const baselineInterest =
        monthlyPayment * payoffMonths - Number(aggregate.total_loan_debt || 0);
    const monthlyInterest = payoffMonths ? baselineInterest / payoffMonths : 0;
    const acceleratedMonths = amortizeLoan(
        aggregate.total_loan_debt,
        aggregate.average_interest_rate,
        monthlyPayment,
        extraPayment
    );
    const interestSavings = Math.max(
        0,
        baselineInterest - monthlyInterest * acceleratedMonths
    );
    const currentAge = getAgeAtDate(user.userDob, new Date());
    const improvedPayoffDate = new Date();
    improvedPayoffDate.setMonth(improvedPayoffDate.getMonth() + acceleratedMonths);
    const improvedPayoffAge = getAgeAtDate(user.userDob, improvedPayoffDate);
    const chartEndAge = Math.max(payoffAge || currentAge, improvedPayoffAge || currentAge) + 5;
    const chartAgeRange = Math.max(chartEndAge - (currentAge || 0), 1);
    const improvedChartX = Math.max(
        0,
        Math.min(161, ((improvedPayoffAge - currentAge) / chartAgeRange) * 161)
    );
    const currentChartX = Math.max(
        0,
        Math.min(161, ((payoffAge - currentAge) / chartAgeRange) * 161)
    );
    const firstName = data.userInfo[0].firstName && data.userInfo[0].firstName.length >= 2
        ? data.userInfo[0].firstName
        : data.userInfo[0].userEmail;

    return (
        <div className={styles["contain"]}>
        <div className={styles["scroll-view"]}>
        <div className={styles["row-view"]}>
        <Menu data={data} />
        <img
    src={logo}
    alt="AlumSum"
    className={styles["image"]}
    />
    </div>
    <span className={styles["text"]} >
        Welcome to your AlumSum dashboard, {firstName}
    </span>
    <span className={styles["text2"]} >
        Quick Stats
    </span>
    <div className={styles["row-view2"]}>
    <div className={styles["column2"]}>
    <span className={styles["text3"]} >
        {formatCurrency(aggregate.total_loan_debt)}
    </span>
    <span className={styles["text4"]} >
        Principal remaining.
    </span>
    </div>
    <div className={styles["column3"]}>
    <span className={styles["text5"]} >
        {payoffAge ? `${payoffAge} yo` : "—"}
    </span>
    <span className={styles["text6"]} >
        You’ll be debt-free {payoffDate}.
    </span>
    </div>
    <div className={styles["column4"]}>
    <span className={styles["text7"]} >
        {aggregate.average_interest_rate}%
        </span>
        <span className={styles["text8"]} >
        Average interest rate of your loans.
    </span>
    </div>
    </div>
    <div className={styles["column5"]}>
    <span className={styles["text9"]} >
        My Loans
    </span>
    <div className={styles["column6"]}>
    <div className={styles["column7"]}>
    <span className={styles["text10"]} >
        Total Principal
    </span>
    <div className={styles["row-view3"]}>
    <div
        className={`${styles["row-view4"]} ${styles["loan-summary-paid-principal"]}`}
        style={{ flexGrow: principalPaidRatio }}
    >
    <span className={styles["text11"]} >
        {formatCurrency(principalPaid)}
    </span>
    </div>
    <div
        className={`${styles["row-view4"]} ${styles["loan-summary-remaining"]}`}
        style={{ flexGrow: principalRemainingRatio }}
    >
        <span className={styles["text12"]}>{formatCurrency(principalRemaining)}</span>
        <span className={styles["text13"]}>remaining</span>
    </div>
    </div>
    <span className={styles["text10"]} >
        Total Interest
    </span>
    <div className={styles["row-view5"]}>
    <div
        className={`${styles["view2"]} ${styles["loan-summary-paid-interest"]}`}
        style={{ flexGrow: interestPaidRatio }}
    >
        <span className={styles["text11"]}>{formatCurrency(interestPaid)}</span>
    </div>
    <div
        className={`${styles["view2"]} ${styles["loan-summary-remaining"]}`}
        style={{ flexGrow: interestRemainingRatio }}
    >
        <span className={styles["text12"]}>{formatCurrency(totalInterest - interestPaid)}</span>
    <span className={styles["text13"]} >
        (estimated)
        </span>
    </div>
        </div>
        </div>
        <div className={styles["column10"]}>
    <div className={styles["row-view6"]}>
    <span className={styles["text14"]} >
        Loan Provider
    </span>
    <div className={styles["box3"]}>
        </div>
        <span className={styles["text15"]} >
        Balance
        </span>
        <div className={styles["column11"]}>
    <img
        src={"https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pXwaee55hr/t1t2fuzb_expires_30_days.png"}
    className={styles["image4"]}
    />
    <img
    src={"https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pXwaee55hr/uietb0xz_expires_30_days.png"}
    className={styles["image5"]}
    />
    <img
    src={"https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pXwaee55hr/orrmesm0_expires_30_days.png"}
    className={styles["image6"]}
    />
    </div>
    <div className={styles["view3"]}>
    <div className={styles["column12"]}>
    <img
        src={"https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pXwaee55hr/5qv1nxci_expires_30_days.png"}
    className={styles["image7"]}
    />
    <img
    src={"https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pXwaee55hr/2mpp646s_expires_30_days.png"}
    className={styles["image8"]}
    />
    </div>
    </div>
    <span className={styles["text14"]} >
        Interest
        </span>
        </div>
        <div className={styles["loan-divider"]} />
    {providerRows.map((provider, index) => (
        <div className={styles[index === providerRows.length - 1 ? "row-view8" : "row-view7"]} key={provider.name}>
            <span className={styles[index === providerRows.length - 1 ? "text18" : "text16"]}>
                {provider.name}{"\n"}({provider.count} {provider.count === 1 ? "loan" : "loans"})
            </span>
            <div className={styles["box3"]} />
            <span className={styles["text17"]}>{formatCurrency(provider.balance)}</span>
            <span className={styles["text12"]}>{provider.interest.toFixed(2)}%</span>
        </div>
    ))}
        <div className={styles["loan-divider"]} />
    <div className={styles["row-view9"]}>
    <span className={styles["text14"]} >
        Total / Average
        </span>
        <div className={styles["box3"]}>
        </div>
        <span className={styles["text19"]} >
        {formatCurrency(aggregate.total_loan_debt)}
    </span>
    <span className={styles["text14"]} >
        {aggregate.average_interest_rate}%
        </span>
        </div>
        <div className={styles["row-view10"]}>
    <img
        src={"https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pXwaee55hr/0jtoa5em_expires_30_days.png"}
    className={styles["image11"]}
    />
    <span className={styles["text20"]} >
        Recommended
        </span>
        <div className={styles["box4"]}>
        </div>
        </div>
        </div>
        <button className={styles["button"]}
    onClick={()=>alert("Pressed!")}>
    <span className={styles["text21"]} >
        Make Payment
    </span>
    </button>
    </div>
    </div>
    <div className={styles["column19"]}>
    <span className={styles["text9"]} >
        Payoff Timeline
    </span>
    <div className={styles["column20"]}>
    <div className={styles["column21"]}>
    <span className={styles["text30"]} >
        Drag the slider to see how an extra monthly payment of {formatCurrency(extraPayment)} will shorten your payoff timeline.
    </span>
    <label className={styles["text30"]} htmlFor="mobile-extra-payment">
        Extra payment: {formatCurrency(extraPayment)}
    </label>
    <input
        id="mobile-extra-payment"
        type="range"
        min="0"
        max="1000"
        step="10"
        value={extraPayment}
        onChange={(event) => setExtraPayment(Number(event.target.value))}
        aria-label="Extra monthly payment"
        className={styles["payment-slider"]}
    />
    </div>
    <div className={styles["column22"]}>
    <div className={styles["column23"]}>
    <div className={styles["column24"]}>
    <div className={styles["column25"]}>
    <img
        src={"https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pXwaee55hr/ke2p6af2_expires_30_days.png"}
    className={styles["absolute-image2"]}
    />
    <span className={styles["text14"]} >
        $70.9k
    </span>
    </div>
    <div className={styles["column26"]}>
    <div className={styles["timeline-legend"]}>
        <span className={styles["timeline-legend-item"]}>
            <span className={styles["timeline-dot-improved"]} />
            Improved Track
        </span>
        <span className={styles["timeline-legend-item"]}>
            <span className={styles["timeline-dot-current"]} />
            Current Track
        </span>
    </div>
    <svg className={styles["image15"]} viewBox="0 0 180 215" role="img" aria-label="Payoff timeline chart">
        <line className={styles["timeline-axis"]} x1="12" y1="12" x2="12" y2="168" />
        <line className={styles["timeline-axis"]} x1="12" y1="168" x2="168" y2="168" />
        <text className={styles["timeline-axis-label"]} x="6" y="92" transform="rotate(-90 6 92)">
            Remaining Balance
        </text>
        <text className={styles["timeline-axis-label"]} x="90" y="205" textAnchor="middle">
            Your Age
        </text>
        <line className={styles["timeline-tick"]} x1="12" y1="168" x2="12" y2="173" />
        <text className={styles["timeline-value"]} x="12" y="187" textAnchor="middle">
            {currentAge}
        </text>
        <line
            className={styles["timeline-tick-current"]}
            x1={12 + currentChartX}
            y1="168"
            x2={12 + currentChartX}
            y2="173"
        />
        <text
            className={styles["timeline-value-current"]}
            x={12 + currentChartX}
            y="187"
            textAnchor="middle"
        >
            {payoffAge}
        </text>
        <line
            className={styles["timeline-tick-improved"]}
            x1={12 + improvedChartX}
            y1="168"
            x2={12 + improvedChartX}
            y2="173"
        />
        <text
            className={styles["timeline-value-improved"]}
            x={12 + improvedChartX}
            y="187"
            textAnchor="middle"
        >
            {improvedPayoffAge}
        </text>
        <path
            className={styles["timeline-current"]}
            d={`M 12 18 C ${12 + currentChartX * 0.25} 24, ${12 + currentChartX * 0.75} 140, ${12 + currentChartX} 168`}
        />
        <path
            className={styles["timeline-improved"]}
            d={`M 12 18 C ${12 + improvedChartX * 0.25} 24, ${12 + improvedChartX * 0.75} 155, ${12 + improvedChartX} 168`}
        />
    </svg>
    </div>
    </div>
        </div>
        </div>
        </div>
        <div className={styles["column13"]}>
    <span className={styles["text9"]} >
    Calendar
    </span>
    <div className={styles["column27"]}>
    <div className={styles["mobile-calendar-picker"]}>
    <DayPicker
    month={new Date()}
    modifiers={calendarDates}
    modifiersStyles={calendarDateStyles}
    />
    </div>
    <div className={styles["column35"]}>
    <div className={styles["row-view16"]}>
    <span className={styles["text53"]} >
        Loan Provider
    </span>
    <span className={styles["text53"]} >
        Amount
        </span>
        <span className={styles["text53"]} >
        Due Date
    </span>
    </div>
        {calendarRows.map((row, index) => (
    <div className={styles[index === calendarRows.length - 1 ? "row-view18" : "row-view17"]} key={`${row.providerId}-${index}`}>
        <span className={styles["text12"]}>{row.name}</span>
        <div className={styles["box3"]} />
        <span className={styles["text12"]}>
            {row.dueDate === "Under Forbearance" ? "—" : formatCurrency(row.amount)}
        </span>
        <div className={styles["box3"]} />
        <span className={styles["text12"]}>
            {row.dueDate === "Under Forbearance"
                ? row.dueDate
                : formatCalendarDate(row.dueDate)}
        </span>
    </div>
        ))}
    </div>
    <button className={styles["button3"]}
    onClick={()=>alert("Pressed!")}>
    <span className={styles["text21"]} >
        Set Payment Reminders
    </span>
    </button>
    </div>
    </div>
    <div className={styles["view4"]}>
    <span className={styles["text59"]} >
						© 2020 alumsum   |   Privacy Statement   |   Terms of Use
    </span>
    </div>
    </div>
    </div>
    </div>
)
}